import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './components/notifications';
import { HelpProvider } from './contexts/HelpContext';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Availability from './pages/Availability';
import TrainingSessions from './pages/TrainingSessions';
import Equipment from './pages/Equipment';
import Crews from './pages/Crews';
import TrainingGroups from './pages/TrainingGroups';
import Planning from './pages/Planner';
import Settings from './pages/Settings';
import NotificationsPage from './pages/Notifications';
import Autoplanner from './pages/Autoplanner';
import LoadingSpinner from './components/LoadingSpinner';
import SentryDebugComponent from './components/debug/SentryDebugComponent';
import { PWANotificationHandler, InAppNotificationHandler } from './components/notifications';
import { PWAManager, PWAStatus } from './components/pwa';
import { useAutoRequestPushPermission } from './hooks/usePushNotifications';
import Help from './pages/Help';
import Admin from './pages/Admin';
import SubscriptionProvider from './contexts/SubscriptionContext.jsx';
import React, { useEffect } from 'react';

function App() {
  const { user, loading } = useAuth();

  // Global cleanup effect to ensure PWA banner CSS properties are cleaned up
  useEffect(() => {
    // Cleanup function to remove any lingering PWA banner CSS properties
    const cleanup = () => {
      document.body.classList.remove('open-app-banner-visible');
      document.documentElement.style.removeProperty('--open-app-banner-height');
    };

    // Clean up on mount and unmount
    cleanup();

    return cleanup;
  }, []);

  // Auto-request is moved into a child component rendered inside SubscriptionProvider
  const pushEnabled = import.meta.env.VITE_PUSH_NOTIFICATIONS_ENABLED === 'true';

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <NotificationProvider>
      <HelpProvider>
        <SubscriptionProvider>
          <DndProvider backend={HTML5Backend}>
            <div className="h-screen overflow-auto bg-gray-50">
              {/* PWA Components */}
              <PWANotificationHandler />
              <InAppNotificationHandler />
              <PWAStatus />

              {/* Ensure auto-permission runs under SubscriptionProvider */}
              <PushSetupGate enabled={!!user && pushEnabled} />

              <Routes>
                {!user ? (
                  // Public routes
                  <>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                    <Route path="*" element={<Navigate to="/login" replace />} />
                  </>
                ) : (
                  // Protected routes
                  <Route path="/" element={<Layout />}>
                    <Route index element={<Dashboard />} />
                    <Route path="availability" element={<Availability />} />
                    <Route path="training-sessions" element={<TrainingSessions />} />
                    <Route path="planner" element={<Planning />} />
                    <Route path="equipment" element={<Equipment />} />
                    <Route path="crews" element={<Crews />} />
                    <Route path="training-groups" element={<TrainingGroups />} />
                    <Route path="admin" element={<Admin />} />
                    <Route path="notifications" element={<NotificationsPage />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="autoplanner" element={<Autoplanner />} />
                    <Route path="help" element={<Help />} />
                    {/* Debug route - only in development */}
                    {import.meta.env.VITE_ENVIRONMENT === 'development' && (
                      <Route path="debug/sentry" element={<SentryDebugComponent />} />
                    )}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                )}
              </Routes>
            </div>
          </DndProvider>
        </SubscriptionProvider>
      </HelpProvider>
    </NotificationProvider>
  );
}

export default App;

function PushSetupGate({ enabled }) {
  useAutoRequestPushPermission(Boolean(enabled), 5000);
  return null;
}
